import { useQuery } from '@apollo/client'

import { CLICK_CODE, extractByTypename, isEmptyArray } from '@local/utils'

import { FavoritesSection_UserFragment, FrequentPlaceShortCutType, MySectionType } from '@web/federation/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { openNewWindow, useGoTo } from '@web/hooks/useGoTo'
import routes from '@web/libs/constants/routes'
import { Compare, compareVersion, mapsVersions } from '@web/libs/entry.helper'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import FavoritesSectionQuery from '@web/pages/v5/home/FavoriteSection/query'
import { MySectionsQuery } from '@web/pages/v5/libs/graphql/MySections'
import UIFavoriteSection, { FrequentPlacePrimitive } from '@web/ui/pages/home/components/Sections/UIFavoritesSection'

const DEFAULT_HOME = {
  id: 'home',
  name: '집',
  alias: '집',
  shortcutType: FrequentPlaceShortCutType.Home,
  directionLandingUrl: {
    car: {
      appScheme: undefined,
      webUrl: undefined,
    },
  },
}

const DEFAULT_OFFICE = {
  id: 'office',
  name: '회사 / 학교',
  alias: '회사 / 학교',
  shortcutType: FrequentPlaceShortCutType.Office,
  directionLandingUrl: {
    car: {
      appScheme: undefined,
      webUrl: undefined,
    },
  },
}

const DEFAULT_ETC = {
  id: 'etc',
  name: '자주 가는 곳',
  alias: '자주 가는 곳',
  shortcutType: FrequentPlaceShortCutType.Etc,
  directionLandingUrl: {
    car: {
      appScheme: undefined,
      webUrl: undefined,
    },
  },
}

const useConvertProps = () => {
  const { data } = useQuery(MySectionsQuery, {
    fetchPolicy: 'cache-first',
  })
  const { MySectionsSucceed } = extractByTypename(data?.mySections)
  const { goToDirectionLandingUrl } = useGoTo()
  const { isMobile } = useGlobalStaticStateContext()

  const { FrequentPlaceSection } = extractByTypename(
    MySectionsSucceed?.sections.find(section => section.type === MySectionType.FrequentPlaceSection),
  )
  const { myHomeListLandingUrl, myHomeEditLandingUrl, myWorkplaceEditLandingUrl, frequentPlaceListLandingUrl } =
    FrequentPlaceSection ?? {}

  return {
    onClickTitle: () => {
      sendClickLog({
        area: CLICK_CODE['myfavplace.title'],
      })
      if (!isMobile) {
        openNewWindow(routes.PC_MAP_FAVORITES_HOME_AND_OFFICE)
        return
      }

      goToDirectionLandingUrl(myHomeListLandingUrl)
    },
    onClickEditMyHome: () => {
      if (!isMobile) {
        openNewWindow(routes.PC_MAP_FAVORITES_HOME_AND_OFFICE)
        return
      }
      goToDirectionLandingUrl(myHomeEditLandingUrl)
    },
    onClickEditMyWorkplace: () => {
      if (!isMobile) {
        openNewWindow(routes.PC_MAP_FAVORITES_HOME_AND_OFFICE)
        return
      }
      goToDirectionLandingUrl(myWorkplaceEditLandingUrl)
    },
    onClickFrequentPlaceList: () => {
      if (!isMobile) {
        openNewWindow(routes.PC_MAP_FAVORITES_PLACES)
        return
      }
      goToDirectionLandingUrl(frequentPlaceListLandingUrl)
    },
  }
}

interface Props {
  user?: FavoritesSection_UserFragment
}

const FavoritesSection = ({ user }: Props) => {
  const { data } = useQuery(FavoritesSectionQuery)
  const { MeSucceed } = extractByTypename(data?.me)

  const frequentPlaces = (MeSucceed?.frequentPlaces || []).map(place => ({
    ...place,
    directionLandingUrl: place.directionLandingUrl
      ? {
          car: {
            appScheme: place.directionLandingUrl.car?.appScheme || undefined,
            webUrl: place.directionLandingUrl.car?.webUrl || undefined,
          },
        }
      : {
          car: {
            appScheme: undefined,
            webUrl: undefined,
          },
        },
  })) as FrequentPlacePrimitive[]
  const { onClickTitle, onClickEditMyHome, onClickEditMyWorkplace, onClickFrequentPlaceList } = useConvertProps()
  const { goToLogin, goToDirectionLandingUrl } = useGoTo()
  const { isFromMapsApp, mapAppVersion } = useGlobalStaticStateContext()

  const home =
    frequentPlaces.find(place => place.shortcutType === FrequentPlaceShortCutType.Home) ||
    (DEFAULT_HOME as FrequentPlacePrimitive)
  const office =
    frequentPlaces.find(place => place.shortcutType === FrequentPlaceShortCutType.Office) ||
    (DEFAULT_OFFICE as FrequentPlacePrimitive)
  const school = frequentPlaces.find(
    place => place.shortcutType === FrequentPlaceShortCutType.School,
  ) as FrequentPlacePrimitive
  const allEtcs = frequentPlaces.filter(place => place.shortcutType === FrequentPlaceShortCutType.Etc)
  const etcs = allEtcs.length > 0 ? allEtcs.slice(0, 4) : ([DEFAULT_ETC] as [FrequentPlacePrimitive])

  const getTitle = (): string => {
    if (isEmptyArray(frequentPlaces)) return '자주 찾는 장소를 등록해보세요.'
    return isFromMapsApp ? '즐겨찾기' : '장소 즐겨찾기'
  }

  const onClickFindFastestPath = (favoriteItem?: FrequentPlacePrimitive): void => {
    if (favoriteItem) {
      goToDirectionLandingUrl(favoriteItem.directionLandingUrl.car)
    }
  }

  const isOnClickTitleValid =
    isFromMapsApp && mapAppVersion
      ? compareVersion(mapsVersions.FAVORITE_LANDING_SDK, mapAppVersion) > Compare.SMALLER
      : true

  return (
    <UIFavoriteSection
      title={getTitle()}
      isOnClickTitleValid={isOnClickTitleValid}
      home={home}
      office={office}
      school={school}
      etcs={etcs}
      onClickTitle={onClickTitle}
      onClickEditMyHome={!!user?.idno ? onClickEditMyHome : () => goToLogin()}
      onClickEditMyWorkplace={!!user?.idno ? onClickEditMyWorkplace : () => goToLogin()}
      onClickFrequentPlaceList={!!user?.idno ? onClickFrequentPlaceList : () => goToLogin()}
      onClickFindFastestPath={!!user?.idno ? onClickFindFastestPath : () => goToLogin()}
    />
  )
}

export default FavoritesSection
